/**
 * API client per la gestione dei proprietari (owners).
 *
 * Endpoints:
 * - GET /api/v1/owners/ - Lista proprietari
 * - GET /api/v1/owners/{id} - Dettaglio con pazienti
 * - GET /api/v1/owners/{id}/history - Storico completo
 */

import axios from 'axios';

export interface PatientSummary {
  id: string;
  name: string;
  species: string | null;
  breed: string | null;
  cases_count: number;
}

export interface OwnerItem {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  first_seen_at: string;
  last_seen_at: string | null;
  patients_count: number;
}

export interface OwnerDetail {
  id: string;
  email: string;
  email_aliases: string[];
  full_name: string | null;
  phone: string | null;
  first_seen_at: string;
  last_seen_at: string | null;
  notes: string | null;
  patients: PatientSummary[];
}

export interface OwnerHistoryCaseThread {
  id: string;
  subject: string | null;
  emails_count: number;
}

export interface OwnerHistoryCase {
  id: string;
  title: string | null;
  specialty: string | null;
  status: string | null;
  opened_at: string | null;
  threads: OwnerHistoryCaseThread[];
}

export interface OwnerHistoryPatient {
  id: string;
  name: string;
  species: string | null;
  breed: string | null;
  cases: OwnerHistoryCase[];
}

export interface OwnerHistoryOwner {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
}

export interface OwnerHistory {
  owner: OwnerHistoryOwner;
  patients: OwnerHistoryPatient[];
  total_cases: number;
  total_emails: number;
}

const api = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export interface OwnerCreate {
  email: string;
  full_name?: string;
  phone?: string;
  notes?: string;
}

export interface OwnerUpdate {
  full_name?: string;
  phone?: string;
  notes?: string;
  email_aliases?: string[];
}

export const ownersApi = {
  /**
   * Lista tutti i proprietari.
   */
  list: async (limit: number = 50, offset: number = 0): Promise<OwnerItem[]> => {
    const response = await api.get<OwnerItem[]>('/owners/', {
      params: { limit, offset },
    });
    return response.data;
  },

  /**
   * Ottieni dettaglio proprietario con pazienti.
   */
  get: async (ownerId: string): Promise<OwnerDetail> => {
    const response = await api.get<OwnerDetail>(`/owners/${ownerId}`);
    return response.data;
  },

  /**
   * Ottieni storico completo del proprietario.
   */
  getHistory: async (ownerId: string): Promise<OwnerHistory> => {
    const response = await api.get<OwnerHistory>(`/owners/${ownerId}/history`);
    return response.data;
  },

  /**
   * Crea un nuovo proprietario.
   */
  create: async (data: OwnerCreate): Promise<OwnerDetail> => {
    const response = await api.post<OwnerDetail>('/owners/', data);
    return response.data;
  },

  /**
   * Aggiorna un proprietario.
   */
  update: async (ownerId: string, data: OwnerUpdate): Promise<OwnerDetail> => {
    const response = await api.patch<OwnerDetail>(`/owners/${ownerId}`, data);
    return response.data;
  },

  /**
   * Elimina un proprietario.
   */
  delete: async (ownerId: string): Promise<void> => {
    await api.delete(`/owners/${ownerId}`);
  },
};
